import { AppProps } from './App';
import { observer } from 'mobx-react-lite';
import MessageListItem from './MessageListItem';
import { useEffect, useRef } from 'react';
import { StyleState } from '../stores/styleStore';
import { AppState } from '../stores/appStateStore';
import { action } from 'mobx';
import TableHeader from './TableHeader';

function curryOnMouseMoveResize(state: AppState, styles: StyleState, col0: string, col1: string) {
    return action((event: { clientX: number; }) => {
        let dx = event.clientX - state.cursorXPos;
        state.cursorXPos = event.clientX;
        const colStyles0 = styles.get('email-table.' + col0);
        let width0 = colStyles0.rawCss?.width;
        let widthNum0: number|undefined;
        if(width0 && typeof width0 === 'string') {
            width0 = width0.substring(0, width0.length-2);
            widthNum0 = Number.parseInt(width0);
            if(widthNum0 + dx < 60) {
                dx = -1 * widthNum0 + 60;
            }
        }
        
        const colStyles1 = styles.get('email-table.' + col1);
        let width1 = colStyles1.rawCss?.width;
        if(width1 && typeof width1 === 'string') {
            width1 = width1.substring(0, width1.length-2);
            let widthNum1 = Number.parseInt(width1);
            if(widthNum1 - dx < 60) {
                dx = widthNum1 - 60;
            }
            widthNum1 -= dx;
            colStyles1.rawCss!.width! = widthNum1 + 'px';
        }

        // This block is down here after the second dx change
        // to ensure both of them have parity
        if(widthNum0 !== undefined) { 
            widthNum0 += dx;
            colStyles0.rawCss!.width! = widthNum0 + 'px';
        }
    });
} // curryOnMouseMoveResize()

function curryOnMouseUpResize(mouseMoveListener: (event: { clientX: number; }) => void ) {
    return (event: Event) => {
        document.removeEventListener('mousemove', mouseMoveListener);
    };
} // curryOnMouseUpResize()

function curryOnMouseDownResize(
    state: AppState, styles: StyleState, col0: string, col1: string, 
    existingMouseupListenerPointer:  React.MutableRefObject<((event: Event) => void)>,
){
    return (event: { clientX: number; }) => {
        state.cursorXPos = event.clientX;
        const mouseMoveResize = curryOnMouseMoveResize(state, styles, col0, col1);
        if(existingMouseupListenerPointer.current) {
            document.removeEventListener('mouseup', existingMouseupListenerPointer.current);
        }
        document.addEventListener('mousemove', mouseMoveResize);
        existingMouseupListenerPointer.current = curryOnMouseUpResize(mouseMoveResize);
        document.addEventListener('mouseup', existingMouseupListenerPointer.current);
    };
} // curryOnMouseDownResize()

export const PAGE_LENGTH = 500;

const MessageListPage = observer((props: AppProps) => {
    const {state, styles} = props;

    const subjectColRef: React.MutableRefObject<HTMLTableCellElement|null> = useRef(null);
    const fromColRef: React.MutableRefObject<HTMLTableCellElement|null> = useRef(null);
    const toColRef: React.MutableRefObject<HTMLTableCellElement|null> = useRef(null);
    const dateColRef: React.MutableRefObject<HTMLTableCellElement|null> = useRef(null);
    // const sizeColRef: React.MutableRefObject<HTMLTableCellElement|null> = useRef(null);

    const subjectMouseupListenerRef: React.MutableRefObject<((event: Event) => void)> = useRef(() => {;});
    const fromMouseupListenerRef: React.MutableRefObject<((event: Event) => void)> = useRef(() => {;});
    const toMouseupListenerRef: React.MutableRefObject<((event: Event) => void)> = useRef(() => {;});
    const dateMouseupListenerRef: React.MutableRefObject<((event: Event) => void)> = useRef(() => {;});
    // const sizeMouseupListenerRef: React.MutableRefObject<((event: Event) => void)> = useRef(() => {;});

    useEffect(() => {
        const subjectMdown = curryOnMouseDownResize(state, styles, 'subject', 'from', subjectMouseupListenerRef);
        const fromMdown = curryOnMouseDownResize(state, styles, 'from', 'to', fromMouseupListenerRef);
        const toMdown = curryOnMouseDownResize(state, styles, 'to', 'date', toMouseupListenerRef);
        const dateMdown = curryOnMouseDownResize(state, styles, 'date', 'size', dateMouseupListenerRef);
        // const sizeMdown = curryOnMouseDownResize(state, styles, 'size', sizeMouseupListenerRef);

        subjectColRef.current?.addEventListener('mousedown', subjectMdown);
        fromColRef.current?.addEventListener('mousedown', fromMdown);
        toColRef.current?.addEventListener('mousedown', toMdown); 
        dateColRef.current?.addEventListener('mousedown', dateMdown);
        // sizeColRef.current?.addEventListener('mousedown', sizeMdown);
        return () => {
            subjectColRef.current?.removeEventListener('mousedown', subjectMdown);
            fromColRef.current?.removeEventListener('mousedown', fromMdown);
            toColRef.current?.removeEventListener('mousedown', toMdown);
            dateColRef.current?.removeEventListener('mousedown', dateMdown);
            // sizeColRef.current?.removeEventListener('mousedown', sizeMdown);
        };
    });

    return (<div id="messageListPage">
        {
            <table className="prevent-select">
                <thead>
                <tr>
                    {/* We specifically omit the style from the first element here so that it will be flexy and "fill" the rest*/}
                    <th data-type="text-short" >
                        <TableHeader state={state} column={'subject'} ref={subjectColRef} />
                    </th>
                    <th data-type="text-short" style={{width: styles.get('email-table.from').rawCss?.width}}>
                        <TableHeader state={state} column={'from'} ref={fromColRef} />
                    </th>
                    <th data-type="text-short" style={{width: styles.get('email-table.to').rawCss?.width}}>
                        <TableHeader state={state} column={'to'} ref={toColRef} />
                    </th>
                    <th data-type="text-short" style={{width: styles.get('email-table.date').rawCss?.width}}>
                        <TableHeader state={state} column={'date'} ref={dateColRef} />
                    </th>
                    <th data-type="text-short" style={{width: styles.get('email-table.size').rawCss?.width}}>
                        <TableHeader state={state} column={'size'}/>
                    </th>
                </tr>
                </thead>
                <tbody>
                    {
                        state.messageQueryAndIxList.filteredIxList.slice(state.sorting.page*PAGE_LENGTH, state.sorting.page*PAGE_LENGTH+PAGE_LENGTH).map(ix => {
                            return (<MessageListItem
                                state={state}
                                styles={styles} 
                                messageIx={ix}
                                key={ix}              
                            ></MessageListItem>);
                        })
                    }
                </tbody>
            </table>
        }
    </div>);
});

export default MessageListPage;